import type { PointData } from 'pixi.js';
import { Container } from 'pixi.js';
import { getLocalizedText } from '@softgames/localization';
import { Signal } from 'typed-signals';
import type { RaceAvatarFrameView } from './RaceAvatarFrameView';
import type { RaceEventChestView } from '../RaceEventChestView';
import { RaceEventEndSequenceLayout } from '../../../../../layouts/raceEvent/RaceEventEndSequenceLayout';
import { setContainerParent } from '../../../../utils/pixi/transform-utils';
import { registerResizableObject } from '../../../../utils/pixi/registerResizableObject';
import { RaceRankFlagView } from './RaceRankFlagView';
import type { ResourceMap } from '../../../../app/resources/types';
import { InventoryItem } from '../../../../app/resources/types';
import type { InventoryItemAmountView } from '../../../../views/InventoryItemAmountView';
import { createInventoryItemViews } from '../../../../views/InventoryItemAmountView';
import MathUtils from '../../../../utils/MathUtils';
import { triggerParticleFX } from '../../../../libs/particles/triggerParticleFX';

interface RaceEventEndSequenceConfig {
  avatarFrames: RaceAvatarFrameView[];
  chests: RaceEventChestView[];
  playerRank: number;
  coinTarget: Container;
  boosterTarget: Container;
}

export class RaceEventEndSequenceHandler extends Container {
  public readonly onHideDarkOverlay = new Signal();

  public readonly onHideRequest = new Signal();

  private readonly layout = new RaceEventEndSequenceLayout();

  private readonly rankFlags: Container[] = [];

  private readonly rewardBubbles: InventoryItemAmountView[] = [];

  constructor(private readonly config: RaceEventEndSequenceConfig) {
    super();
    this.layout.root.props({
      parent: this,
    });

    registerResizableObject(this);
  }

  public async play(): Promise<void> {
    this.layout.gradient.tweenTo({ alpha: 1 }, 500, 'sinOut', true);

    void this.animateAvatars();
    void this.animateChests();

    await this.wait(500);
    void this.animateText();

    await this.wait(2300);

    this.onHideRequest.emit();

    if (this.config.playerRank <= 3) {
      void this.hideElements();
      await this.wait(400);
      await this.playWinSequence();
    } else {
      await this.hideElements();
    }
  }

  private async playWinSequence(): Promise<void> {
    const {
      config: { playerRank, chests },
      layout: { chestSlot, root, chestRays },
    } = this;

    const index = playerRank - 1;

    const slot = chestSlot[index];
    const chest = chests[index];

    setContainerParent(slot, root);

    chest.tweenTo({ scaleXY: 1 }, 1000, 'sinInOut', true);
    await slot
      .tweenTo({ x: 0, y: slot.pixi.renderer.height * 0.65, scaleXY: 1.1 }, 1000, 'sinInOut', true)
      .asyncOnComplete();

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const { promise, resolve } = Promise.withResolvers<void>();

    await chest.animateOpen(async (spawnPoint, rewards) => {
      this.spawnGifts(spawnPoint.getGlobalPosition(), rewards);
      chestRays[index].tweenTo({ alpha: 1, scaleXY: 0.5 }, 300, 'sinOut', true);
      await root.wait(2000);
      await this.animateGiftsToTargets();
      resolve();
    });

    return promise;
  }

  private spawnGifts(spawnPosition: PointData, rewards: ResourceMap): void {
    const { root } = this.layout;

    const bubbles = createInventoryItemViews(rewards).map((view) =>
      view.props({
        parent: root.pixi.stage,
        position: spawnPosition,
        scale: 0,
      }),
    );

    bubbles.forEach(async (bubble, index): Promise<void> => {
      const angle = bubbles.length > 1 ? -2.355 + (index / (bubbles.length - 1)) * 1.57 : -1.57;
      const { x, y } = MathUtils.getPointOnCircle(bubble.x, bubble.y, 200, angle);
      await bubble
        .tweenTo(
          {
            x,
            y,
            scaleXY: 1.5,
          },
          300,
          'appear1',
          true,
          index * 50,
        )
        .asyncOnComplete();
      await triggerParticleFX('Yellow_Glitter_From_Center', bubble);
    });

    this.rewardBubbles.push(...bubbles);
  }

  private async animateGiftsToTargets(): Promise<void> {
    const { coinTarget, boosterTarget } = this.config;

    this.onHideDarkOverlay.emit();
    this.layout.root.tweenTo({ alpha: 0 }, 200, 'sinIn', true);

    await Promise.all(
      this.rewardBubbles.map(async (bubble) => {
        const target = (bubble.getItemType() === InventoryItem.Coin ? coinTarget : boosterTarget).getGlobalPosition();

        bubble.tweenTo({ scaleXY: 0 }, 300, 'cubicIn', true, 700);

        await bubble
          .tweenTo(
            {
              x: target.x,
              y: target.y,
            },
            1000,
            'sinOut',
            true,
          )
          .asyncOnComplete();
      }),
    );
  }

  private async animateAvatars(): Promise<void> {
    const {
      config: { avatarFrames, playerRank },
      layout: { root, frameSlot, fxLayer, rankedFrames },
    } = this;

    avatarFrames.forEach((frame) => {
      setContainerParent(frame, root);
    });

    const avatarsToMove = avatarFrames.slice(0, 3);

    const avatarsToCenter = avatarFrames.slice(3, 5);

    avatarsToMove.forEach(async (frame, index): Promise<void> => {
      await frame.tweenTo({ scaleXY: 0 }, 300, 'disappear1', true, index * 100).asyncOnComplete();
      frame.props({ parent: frameSlot[index], x: 0, y: 0 });
      if (index === 0) {
        void frame.animateDance();
      }
      await frame.tweenTo({ scaleXY: 1 }, 300, 'appear1', true, 100).asyncOnComplete();

      if (playerRank > 3) return;

      const fxConfig = ['Race_Event_Pink_Fireworks', 'Race_Event_Blue_Fireworks', 'Race_Event_Yellow_Fireworks'][index];
      if (!fxConfig) return;
      await frame.wait(index * 50);
      void triggerParticleFX(fxConfig, fxLayer, (frameSlot[index] ?? root).x, rankedFrames.y);
    });

    await this.wait(400);

    avatarsToCenter.forEach((frame, index) => {
      const otherFrame = avatarsToCenter[(index + 1) % avatarsToCenter.length];
      frame.tweenTo({ x: frame.x < otherFrame.x ? -100 : 100 }, 500, 'sinInOut', true);
    });
  }

  private async animateText(): Promise<void> {
    const {
      layout: { loseText, winTextContainer, winText, winEmphesisText },
      config: { playerRank },
    } = this;

    const win = playerRank <= 3;

    const elementToAnimate = win ? winTextContainer : loseText;

    if (win) {
      const translatedPhrase = getLocalizedText(
        ['You reached the 1st place!', 'You reached the 2nd place!', 'You reached the 3rd place!'][playerRank - 1] ??
          '',
      );

      const [line1, line2] = translatedPhrase.split('\\n');

      if (line1.startsWith('*')) {
        winEmphesisText.props({
          text: line1.replace('*', ''),
          y: -40,
        });
        winText.props({
          text: line2,
          y: 40,
        });
      } else {
        winEmphesisText.props({
          text: line2.replace('*', ''),
        });
        winText.props({
          text: line1,
        });
      }
    }

    elementToAnimate.props({
      visible: true,
      scaleXY: 0,
    });

    await elementToAnimate.tweenTo({ scaleXY: 1 }, 500, 'appear1', true).asyncOnComplete();
  }

  private async animateChests(): Promise<void> {
    const {
      config: { chests },
      layout: { chestSlot, chestRays },
    } = this;

    chestRays.forEach((rays) => {
      rays.tweenTo({ angle: 360 }, 20000, 'linear', true, 0, -1);
    });

    chests.forEach((chest, index) => {
      setContainerParent(chest, chestSlot[index]);
      chest.tweenTo({ x: 0, y: 0 }, 500, 'sinInOut', true);
    });

    await this.wait(300);

    chestRays.forEach((rays, index) => {
      rays.tweenTo({ alpha: 1 }, 300, 'sinOut', true);

      const rankFlag = new RaceRankFlagView({ rank: index + 1, normProgress: 1, level: 10 }).props({
        parent: chestSlot[index],
        addAbove: rays,
        y: -80,
      });

      rankFlag.tweenTo({ y: 20 }, 500, 'sinOut', true);

      this.rankFlags.push(rankFlag);
    });
  }

  private async hideElements(): Promise<void> {
    const {
      config: { playerRank, avatarFrames },
      layout: { chestSlot, textContainer },
      rankFlags,
    } = this;

    const excluded = playerRank >= 3 ? chestSlot[playerRank - 1] : null;

    await Promise.all([
      ...avatarFrames.map((frame, index) =>
        frame.tweenTo({ scaleXY: 0 }, 400, 'disappear1', true, index === 0 ? 100 : 0).asyncOnComplete(),
      ),
      ...chestSlot.map(async (chest, index) => {
        await rankFlags[index].tweenTo({ y: -50 }, 200, 'sinIn', true).asyncOnComplete();
        rankFlags[index].visible = false;

        if (excluded === chest) return Promise.resolve();

        return chest.tweenTo({ scaleXY: 0 }, 400, 'disappear1', true).asyncOnComplete();
      }),
      textContainer.tweenTo({ scaleXY: 0 }, 400, 'disappear1', true).asyncOnComplete(),
    ]);
  }

  public resize(width: number, height: number): void {
    const { gradient, textContainer } = this.layout;

    textContainer.props({
      y: height * 0.55,
    });

    gradient.props({
      width: width * 1.1,
      height: height * 1.1,
    });
  }
}
